import { useMemo } from "react";
import { Input, RadioGroup, Select, Textarea } from "../ui";
import { VISIBILITIES, useOptions, yearOptionsOf } from "../scholarships/model";

// One form row of two fields, whatever sits before it
const ROW = "grid grid-cols-1 gap-5 md:col-span-2 md:grid-cols-2";

/**
 * The details every call has, for its create dialog and its Details tab: `field(name)` gives each
 * input its props, `children` sit after the name (a sponsor, a destination), and the limits follow
 * the call's API. The saved academic year stays selectable once retired. `draft`: only the name is
 * needed now (a scholarship draft), so the other fields are not marked required. `belowVisibility`
 * sits right under the visibility (a private scholarship's reason).
 */
export default function CallFields({ field, savedYear, namePlaceholder, quotaLabel, summaryMax, descriptionMax, opensMin, closesMin, visibilities = VISIBILITIES, draft = false, belowVisibility, children }) {
  const required = !draft;
  const { data: options } = useOptions();
  const yearOptions = useMemo(() => yearOptionsOf(options?.academicYears, savedYear), [options, savedYear]);

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <Input label="Name" required maxLength={200} placeholder={namePlaceholder} {...field("name")} className="md:col-span-2" />
      {children}
      {/* The year and how many on one row, the application window on the next */}
      <div className={ROW}>
        <Select label="Academic year" required={required} placeholder="Select an academic year" options={yearOptions} {...field("academicYearId")} />
        <Input type="number" min="1" step="1" label={quotaLabel} placeholder="Leave empty if not fixed" {...field("awardQuota")} />
      </div>
      <div className={ROW}>
        <Input type="date" label="Applications open" required={required} min={opensMin} info="From 00:00 Kigali time" {...field("opensDate")} />
        <Input type="date" label="Applications close" required={required} min={closesMin} info="Until 23:59 Kigali time" {...field("closesDate")} />
      </div>
      <RadioGroup label="Visibility" required options={visibilities} {...field("visibility")} />
      {belowVisibility && <div className="md:col-span-2">{belowVisibility}</div>}
      <Textarea label="Public summary" rows={3} maxLength={summaryMax} placeholder="Who may apply, in plain language. Shown on the HEC website." {...field("publicSummary")} className="md:col-span-2" />
      <Textarea label="Description" required={required} rows={5} maxLength={descriptionMax} {...field("description")} className="md:col-span-2" />
    </div>
  );
}
